/*
Example chat of the /mcp/ hero: plays the conversation once the chat is on screen —
the question is typed into the input, the send button is pressed, the question lands
in the log, the assistant searches, the answer types letter by letter and the log
follows the newest line.

The real chat (filled by mcpPage.js) stays in the DOM for screen readers; an
aria-hidden copy on top does the typing. The copy's log gets the real log's height, so
the chat never resizes. Skip, reduced motion, Tab into the log, a new width or an error
show the real chat at once. The caller's AbortSignal stops everything (PJAX swap).
*/

const START_MS = 500;
const QUESTION_CHAR_MS = 35;
const ANSWER_CHAR_MS = 12;
const PAUSE_MS = 400;
const PRESS_MS = 160;
const THINK_MS = 1800;
const THINKING_TEXT = 'Searching fresh news';
const VISIBLE_SHARE = 0.4;
const VEILED = '.th-mcp__question, .th-mcp__answer-meta, .th-mcp__stories li, .th-mcp__answer-source';

/**
 * Plays the chat once; does nothing under reduced motion or without IntersectionObserver.
 * @param {HTMLElement} chat - [data-mcp-chat] with the answer already filled.
 * @param {AbortSignal} signal - aborts the playback and its listeners.
 */
export default function playChat(chat, signal) {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (motion.matches || !window.IntersectionObserver) {
        return;
    }
    const show = new Playback(chat, motion, signal);

    try {
        show.prepare();
    } catch (error) {
        show.finish();
        throw error;
    }
}

class Playback {
    constructor(chat, motion, signal) {
        this.chat = chat;
        this.motion = motion;
        this.signal = signal;
        this.original = chat.querySelector('.th-mcp__chat-log');
        this.skip = chat.querySelector('.th-mcp__skip');
        this.timer = null;
        this.observer = null;
        this.layer = null;
        this.log = null;
        this.startWidth = window.innerWidth;
        this.listeners = new AbortController();
    }

    prepare() {
        const finish = () => this.finish();
        const options = { signal: this.listeners.signal };

        this.layer = buildLayer(this.chat);
        this.log = this.layer.querySelector('.th-mcp__chat-log');
        this.chat.append(this.layer);
        this.matchLogHeight();
        this.chat.dataset.state = 'playing';
        this.skip.hidden = false;
        this.skip.addEventListener('click', finish, options);
        this.motion.addEventListener('change', finish, options);
        // Another width (rotation, window) relays the real chat out: the copy would drift.
        window.addEventListener('resize', () => {
            if (window.innerWidth !== this.startWidth) {
                this.finish();
            }
        }, options);
        // The real log is reachable by Tab while transparent: playback gives way to focus.
        this.original.addEventListener('focus', finish, options);
        this.signal.addEventListener('abort', finish, options);
        this.startWhenVisible();
    }

    finish() {
        window.clearTimeout(this.timer);
        this.observer?.disconnect();
        this.listeners.abort();
        if (this.layer) {
            this.original.scrollTop = this.original.scrollHeight;
            this.layer.remove();
            this.layer = null;
        }
        delete this.chat.dataset.state;
        // The button disappears: focus moves to the log instead of being lost.
        if (document.activeElement === this.skip) {
            this.original.focus();
        }
        this.skip.hidden = true;
    }

    wait(ms, next) {
        this.timer = window.setTimeout(() => {
            try {
                next();
            } catch (error) {
                this.finish();
                throw error;
            }
        }, ms);
    }

    startWhenVisible() {
        this.observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && this.layer) {
                this.observer.disconnect();
                this.play();
            }
        }, { threshold: VISIBLE_SHARE });
        this.observer.observe(this.chat);
    }

    // Measured at the start, when fonts are applied.
    matchLogHeight() {
        this.log.style.height = `${this.original.offsetHeight}px`;
    }

    follow() {
        this.log.scrollTop = this.log.scrollHeight;
    }

    play() {
        const typed = this.layer.querySelector('.th-mcp__typed');
        const placeholder = typed.textContent;
        const text = questionText(this.layer.querySelector('.th-mcp__question'));

        this.matchLogHeight();
        typed.textContent = '';
        typed.dataset.state = 'typing';
        this.wait(START_MS, () => this.typeInto(typed, text, () => {
            this.wait(PAUSE_MS, () => this.send(typed, placeholder));
        }));
    }

    typeInto(field, text, done) {
        if (field.textContent.length === text.length) {
            done();
            return;
        }
        field.textContent = text.slice(0, field.textContent.length + 1);
        field.scrollLeft = field.scrollWidth;
        this.wait(QUESTION_CHAR_MS, () => this.typeInto(field, text, done));
    }

    send(typed, placeholder) {
        const button = this.layer.querySelector('.th-mcp__send');

        button.dataset.state = 'pressed';
        this.wait(PRESS_MS, () => {
            delete button.dataset.state;
            typed.textContent = placeholder;
            delete typed.dataset.state;
            this.reveal(this.layer.querySelector('.th-mcp__question'));
            this.wait(PAUSE_MS, () => this.answer());
        });
    }

    answer() {
        const answer = this.layer.querySelector('.th-mcp__answer');
        const thinking = thinkingRow();
        const items = [...answer.querySelectorAll('.th-mcp__stories li')];

        answer.prepend(thinking);
        this.follow();
        this.wait(THINK_MS, () => {
            thinking.remove();
            this.reveal(answer.querySelector('.th-mcp__answer-meta'));
            this.wait(PAUSE_MS, () => this.typeItems(items, () => {
                this.reveal(answer.querySelector('.th-mcp__answer-source'));
                this.wait(PAUSE_MS, () => this.finish());
            }));
        });
    }

    typeItems(items, done) {
        const [item, ...rest] = items;

        if (!item) {
            done();
            return;
        }
        const parts = emptyText(item);

        delete item.dataset.veil;
        this.typeParts(parts, () => this.typeItems(rest, done));
    }

    typeParts(parts, done) {
        const [part, ...rest] = parts;

        if (!part) {
            done();
            return;
        }
        if (part.node.nodeValue.length === part.text.length) {
            this.typeParts(rest, done);
            return;
        }
        part.node.nodeValue = part.text.slice(0, part.node.nodeValue.length + 1);
        this.follow();
        this.wait(ANSWER_CHAR_MS, () => this.typeParts(parts, done));
    }

    reveal(element) {
        delete element.dataset.veil;
        element.dataset.state = 'pop';
        this.follow();
    }
}

// The chat's copy at its start: empty log of the same height, input ready to type.
function buildLayer(chat) {
    const copy = document.createElement('div');

    copy.className = 'th-mcp__play';
    copy.setAttribute('aria-hidden', 'true');
    for (const child of chat.children) {
        if (!child.classList.contains('th-mcp__skip')) {
            copy.append(child.cloneNode(true));
        }
    }
    for (const element of copy.querySelectorAll('[id], [tabindex]')) {
        element.removeAttribute('id');
        element.removeAttribute('tabindex');
    }
    for (const element of copy.querySelectorAll(VEILED)) {
        element.dataset.veil = '';
    }

    return copy;
}

// Text nodes of an element: texts remembered, nodes emptied.
function emptyText(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const parts = [];

    while (walker.nextNode()) {
        parts.push({ node: walker.currentNode, text: walker.currentNode.nodeValue });
    }
    for (const part of parts) {
        part.node.nodeValue = '';
    }

    return parts;
}

function questionText(question) {
    const copy = question.cloneNode(true);

    copy.querySelector('.th-visually-hidden')?.remove();

    return copy.textContent.trim();
}

function thinkingRow() {
    const row = document.createElement('p');

    row.className = 'th-mcp__thinking';
    row.textContent = THINKING_TEXT;
    row.append(document.createElement('i'), document.createElement('i'), document.createElement('i'));

    return row;
}
